import { lazy, Suspense, useEffect, useState } from 'react';

import { BAND } from '../report/constants.js';
import { adminFetch } from './adminFetch.js';
import MapView from './MapView.jsx';

/*
 * Loaded on demand, as legacy loaded engagement.js and dii-report.js: an
 * owner never fetches code for views they cannot open.
 */
const Engagement = lazy(() => import('./Engagement.jsx'));
const DiiReport = lazy(() => import('./DiiReport.jsx'));

/**
 * Admin / My Companies — a port of app/public/admin.js.
 *
 * Three ways of looking at the same cohort, because the interesting question
 * differs by audience: geographic spread (county), maturity distribution
 * (band), or specifics (sortable table). Reads GET /api/companies, which
 * applies the owner filter server-side.
 *
 * UI state (tab, owner filter, sort, DII filters) is held by the caller, not
 * here. Legacy kept it at module level, so opening a company's report and
 * coming back restored the tab, filter and sort you left; this component
 * unmounts during a drill-through, so the state lives above it.
 */

export const BAND_ORDER = ['Foundation', 'Developing', 'Established', 'Advanced'];

const PERSONA_SHORT = { A: 'Attraction', B: 'Activity' };

/** Hover detail for a whole-percentage score, so 25% (Foundation) and 25%
    (Developing) can be told apart on review. Never replaces the headline. */
const oneDecimal = (precise) => `${precise.toFixed(1)}% to one decimal place`;
const STATUS_LABEL = {
  not_started: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
};

/** Band pill, identical to the report's. */
export function Pill({ band }) {
  if (!band) return <span className="admin-muted">—</span>;
  return (
    <span className="band-pill band-pill-sm" style={{ background: BAND[band].fill, color: BAND[band].on }}>{band}</span>
  );
}

/**
 * Props that make a row open a company's report — for keyboard and pointer
 * alike. Purely navigational; the server re-checks the staff session and the
 * caller's scope on every request.
 */
function openable(company, onOpen, baseClass) {
  const cls = (extra) => (baseClass ? `${baseClass} ${extra}` : extra);
  if (company.status !== 'complete') {
    return { className: cls('not-openable'), title: 'No completed assessment to open yet' };
  }
  const open = () => onOpen?.(company);
  return {
    className: cls('openable'),
    role: 'button',
    tabIndex: 0,
    title: `Open ${company.companyName}'s report`,
    onClick: open,
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        open();
      }
    },
  };
}

function CompanyRow({ company, onOpen }) {
  return (
    <div {...openable(company, onOpen, 'admin-row')}>
      <div className="admin-row-main">
        <span className="admin-name">{company.companyName}</span>
        <span className="admin-sub">
          {[company.county, company.subSector, PERSONA_SHORT[company.persona]].filter(Boolean).join(' · ')}
        </span>
      </div>
      <div className="admin-row-meta">
        {company.riskFlags > 0 && (
          <span className="admin-flag" title="Priority support flag raised">
            {`${company.riskFlags} flag${company.riskFlags > 1 ? 's' : ''}`}
          </span>
        )}
        {company.overallPercent !== null && (
          <span className="admin-score" title={oneDecimal(company.overallPercentPrecise)}>{`${company.overallPercent}%`}</span>
        )}
        <Pill band={company.overallBand} />
        <span className={`admin-status admin-status-${company.status}`}>{STATUS_LABEL[company.status]}</span>
      </div>
    </div>
  );
}

/* ── Grouped views ───────────────────────────────────────────────── */

/**
 * @param stickyHeads  pin each group's heading to the top of its own column
 *   while that column's cards scroll. Wanted for the band view, where a group
 *   can run to 22 companies; not for the county view, where groups hold one
 *   to four and 22 headings pinning and unpinning would be noise.
 */
function Grouped({ companies, keyOf, order, stickyHeads = false, onOpen }) {
  const groups = new Map();
  for (const company of companies) {
    const key = keyOf(company);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(company);
  }
  const keys = order
    ? order.filter((k) => groups.has(k)).concat([...groups.keys()].filter((k) => !order.includes(k)))
    : [...groups.keys()].sort((a, b) => a.localeCompare(b));

  return (
    <div className={`admin-groups${stickyHeads ? ' sticky-heads' : ''}`}>
      {keys.map((key) => {
        const members = groups.get(key).slice().sort((a, b) => a.companyName.localeCompare(b.companyName));
        return (
          <section key={key} className="admin-group">
            <h3 className="admin-group-head">
              <span>{key}</span>
              {/* Counts, so the spread is quantified rather than only clustered. */}
              <span className="admin-count">{String(members.length)}</span>
            </h3>
            {members.map((company) => <CompanyRow key={company.companyId} company={company} onOpen={onOpen} />)}
          </section>
        );
      })}
    </div>
  );
}

/* ── Sortable table ──────────────────────────────────────────────── */

const COLUMNS = [
  { key: 'companyName', label: 'Company' },
  { key: 'county', label: 'County' },
  { key: 'persona', label: 'Type' },
  { key: 'overallBand', label: 'Digital maturity level' },
  { key: 'overall', label: 'Score' },
  { key: 'status', label: 'Status' },
  { key: 'accessCode', label: 'Access code' },
];

function sortValue(company, key) {
  if (key === 'overallBand') {
    // Band is ordinal, not alphabetical — unscored sorts last.
    const i = BAND_ORDER.indexOf(company.overallBand);
    return i === -1 ? BAND_ORDER.length : i;
  }
  if (key === 'persona') return PERSONA_SHORT[company.persona] ?? '';
  const value = company[key];
  return value === null || value === undefined ? '' : value;
}

function Table({ companies, sort, onSort, onOpen }) {
  const { key, dir } = sort;
  const rows = companies.slice().sort((a, b) => {
    const x = sortValue(a, key);
    const y = sortValue(b, key);
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
    return String(x).localeCompare(String(y)) * dir;
  });

  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <tbody>
          <tr>
            {COLUMNS.map((column) => {
              const active = key === column.key;
              return (
                <th key={column.key} aria-sort={active ? (dir === 1 ? 'ascending' : 'descending') : undefined}>
                  <button
                    type="button"
                    className={`admin-sort${active ? ' active' : ''}`}
                    onClick={() => onSort(active ? { key, dir: -dir } : { key: column.key, dir: 1 })}
                  >
                    {column.label}
                    {active && <span className="admin-arrow">{dir === 1 ? '↑' : '↓'}</span>}
                  </button>
                </th>
              );
            })}
          </tr>
          {rows.map((company) => (
            <tr key={company.companyId} {...openable(company, onOpen)}>
              <td className="admin-cell-name">{company.companyName}</td>
              <td>{company.county ?? '—'}</td>
              <td>{PERSONA_SHORT[company.persona] ?? '—'}</td>
              <td><Pill band={company.overallBand} /></td>
              {/* Shown as a percentage; the column still sorts on the raw
                  1-4 `overall`, which whole percentages would tie. */}
              <td className="admin-num" title={company.overallPercent === null ? undefined : oneDecimal(company.overallPercentPrecise)}>
                {company.overallPercent === null ? '—' : `${company.overallPercent}%`}
              </td>
              <td className={`admin-status admin-status-${company.status}`}>{STATUS_LABEL[company.status]}</td>
              <td className="admin-code">{company.accessCode}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Chrome ──────────────────────────────────────────────────────── */

/**
 * `adminOnly` views are hidden from an owner AND refused by the server. The
 * DII / DETE report is a whole-cohort statistical product; an owner-scoped
 * version of it would be a handful of businesses dressed as a national
 * figure. The tab not rendering is a courtesy — the API is what enforces it.
 */
const VIEWS = [
  { id: 'map', label: 'Map' },
  { id: 'county', label: 'By county' },
  { id: 'band', label: 'By digital maturity level' },
  { id: 'table', label: 'All companies' },
  { id: 'engagement', label: 'Engagement', adminOnly: true },
  { id: 'dii', label: 'Digital Intensity Index / DETE report', adminOnly: true },
];

function Summary({ companies }) {
  const complete = companies.filter((c) => c.status === 'complete').length;
  const counties = new Set(companies.map((c) => c.county).filter(Boolean)).size;
  const flagged = companies.filter((c) => c.riskFlags > 0).length;
  const attractions = companies.filter((c) => c.persona === 'A').length;
  return (
    <div className="admin-summary">
      {[
        [companies.length, 'companies'],
        [complete, `of ${companies.length} complete`],
        [counties, 'counties'],
        [`${attractions}:${companies.length - attractions}`, 'attractions : activities'],
        [flagged, 'priority flags'],
      ].map(([value, label]) => (
        <div key={label} className="admin-stat">
          <span className="admin-stat-value">{String(value)}</span>
          <span className="admin-stat-label">{label}</span>
        </div>
      ))}
    </div>
  );
}

function Controls({ data, view, ownerId, onView, onOwner }) {
  return (
    <div className="admin-controls">
      <div className="admin-tabs" role="tablist">
        {VIEWS.filter((v) => !(v.adminOnly && data.role !== 'admin')).map((v) => (
          <button
            key={v.id}
            type="button"
            className={`admin-tab${view === v.id ? ' active' : ''}`}
            role="tab"
            aria-selected={String(view === v.id)}
            onClick={() => onView(v.id)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Only an admin gets a scope selector, and the server sends an empty
          owners list to anyone else. A non-admin owner is pinned to their
          own companies server-side whatever they send. */}
      {data.role === 'admin' && data.owners.length ? (
        <label className="admin-filter">
          <span>Owner</span>
          <select className="admin-select" value={ownerId} onChange={(event) => onOwner(event.target.value)}>
            <option value="">All (admin)</option>
            {data.owners.map((o) => (
              <option key={o.ownerId} value={o.ownerId}>{`${o.name}${o.role === 'admin' ? ' (admin)' : ''}`}</option>
            ))}
          </select>
        </label>
      ) : (
        <span className="admin-scope-note">Showing the companies assigned to you.</span>
      )}
    </div>
  );
}

export default function Admin({ owner, ui, setUi, onOpenCompany }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Re-queried whenever the owner filter changes; the previous data stays on
  // screen until the new data arrives, as legacy's load() behaved.
  useEffect(() => {
    let live = true;
    const query = ui.ownerId ? `?ownerId=${encodeURIComponent(ui.ownerId)}` : '';
    adminFetch(`/api/companies${query}`, 'Could not load companies.')
      .then((payload) => { if (live) { setData(payload); setError(null); } })
      .catch((err) => { if (live) setError(err.message); });
    return () => { live = false; };
  }, [ui.ownerId]);

  const set = (patch) => setUi((prev) => ({ ...prev, ...patch }));

  let body = null;
  if (error) {
    body = <p className="login-error">{error}</p>;
  } else if (data) {
    const { companies } = data;
    const isAdmin = data.role === 'admin';

    let content;
    if (ui.view === 'map') {
      content = (
        <MapView companies={companies} thresholds={data.bandThresholds} averages={data.averages} onOpenCompany={onOpenCompany} />
      );
    } else if (ui.view === 'county') {
      content = <Grouped companies={companies} keyOf={(c) => c.county ?? 'Unknown'} onOpen={onOpenCompany} />;
    } else if (ui.view === 'band') {
      content = (
        <Grouped
          companies={companies}
          keyOf={(c) => c.overallBand ?? 'Not yet assessed'}
          order={[...BAND_ORDER, 'Not yet assessed']}
          stickyHeads
          onOpen={onOpenCompany}
        />
      );
    } else if (ui.view === 'engagement') {
      content = (
        <Suspense fallback={<div className="dii-report-host"><p className="map-loading">Loading engagement…</p></div>}>
          <Engagement />
        </Suspense>
      );
    } else if (ui.view === 'dii') {
      content = (
        <Suspense fallback={<div className="dii-report-host"><p className="map-loading">Loading report…</p></div>}>
          <DiiReport filters={ui.diiFilters} onFilters={(diiFilters) => set({ diiFilters })} />
        </Suspense>
      );
    } else {
      content = <Table companies={companies} sort={ui.sort} onSort={(sort) => set({ sort })} onOpen={onOpenCompany} />;
    }

    body = (
      <>
        {/* Everything above the data is one pinned block: title, summary
            strip, tabs and owner selector. Only the rows and cards scroll
            beneath it, so the controls stay reachable through a long list. */}
        <div className="admin-chrome">
          <div className="title-block">
            <div className="eyebrow">{isAdmin ? 'Admin' : 'Owner'}</div>
            <h1 className="report-h1">{isAdmin ? 'All companies' : 'My companies'}</h1>
            <p className="report-sub">{`Signed in as ${owner?.name ?? ''}`}</p>
          </div>
          <Summary companies={companies} />
          <Controls data={data} view={ui.view} ownerId={ui.ownerId} onView={(view) => set({ view })} onOwner={(ownerId) => set({ ownerId })} />
        </div>
        <div className="admin-scroll">{content}</div>
      </>
    );
  }

  return (
    <section id="view-admin" className="view">
      <div className="admin-main">
        <div id="admin" className="admin-container">{body}</div>
      </div>
    </section>
  );
}

export const INITIAL_ADMIN_UI = {
  view: 'map',
  ownerId: '',
  sort: { key: 'companyName', dir: 1 },
  diiFilters: { region: '', county: '', persona: '', subSector: '', band: '' },
};
